import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { SSERawEvent } from '../sse-stream/sse-stream-handler.service';

export interface ParsedStreamEvent {
    type: string;
    custom_outputs?: {
        schema?: string;
        event_type?: string;
        span?: any;
        event?: any;
        cognitive?: any;
        datasheets?: any;
        urls?: Array<{
            display_name?: string;
            doc_url?: string;
        }>;
    };
    delta?: string;
    chunk_id?: number;
    item_id?: string;
}

export type EventClassification = 'cognitive' | 'token' | 'completion' | 'datasheet' | 'unknown';

/**
 * Service responsible for parsing and routing SSE events based on their type.
 * Uses SCHEMA-BASED classification, never content patterns.
 */
@Injectable({
    providedIn: 'root'
})
export class StreamEventRouterService {
    // Event streams by type
    private readonly cognitiveEventsSubject$ = new Subject<ParsedStreamEvent>();
    private readonly tokenEventsSubject$ = new Subject<ParsedStreamEvent>();
    private readonly datasheetEventsSubject$ = new Subject<ParsedStreamEvent>();
    private readonly completionEventsSubject$ = new Subject<void>();

    public readonly cognitiveEvents$ = this.cognitiveEventsSubject$.asObservable();
    public readonly tokenEvents$ = this.tokenEventsSubject$.asObservable();
    public readonly datasheetEvents$ = this.datasheetEventsSubject$.asObservable();
    public readonly completionEvents$ = this.completionEventsSubject$.asObservable();

    /**
     * Process raw SSE event and route to appropriate handler
     */
    processRawEvent(event: SSERawEvent): void {
        try {
            // Handle completion signal
            if (event.data === '[DONE]' || event.data.trim() === '[DONE]') {
                this.completionEventsSubject$.next();
                return;
            }

            // Parse the event
            const parsed = this.parseEventData(event.data);
            if (!parsed) {
                console.warn('❓ Could not parse event:', event.data.substring(0, 100));
                return;
            }

            // Route based on classification
            this.routeEvent(parsed);
        } catch (error) {
            console.error('❌ Error processing SSE event:', error);
        }
    }

    /**
     * Parse event data from various formats
     */
    private parseEventData(data: string): ParsedStreamEvent | null {
        try {
            // Try standard JSON first
            return JSON.parse(data);
        } catch {
            // Try Python format conversion
            try {
                const jsonData = data
                    .replace(/'/g, '"')
                    .replace(/None/g, 'null')
                    .replace(/True/g, 'true')
                    .replace(/False/g, 'false');
                return JSON.parse(jsonData);
            } catch {
                return null;
            }
        }
    }

    /**
     * Route parsed event to appropriate handler
     */
    private routeEvent(event: ParsedStreamEvent): void {
        // CHECK FOR FINAL RESPONSE SPAN MARKERS FIRST
        const customOutputs = event.custom_outputs as any;

        // Check for any span markers
        if (customOutputs?.response_span) {
            console.log('🔍 SPAN MARKER FOUND:', {
                response_span: customOutputs.response_span,
                span_type: customOutputs.span_type,
                step: customOutputs.step,
                event_type: customOutputs.event_type,
                full_custom_outputs: customOutputs
            });
        }

        // Check specifically for final response span markers
        if (customOutputs?.response_span && customOutputs?.span_type === 'FINAL_RESPONSE') {
            console.log('🎯 FINAL RESPONSE SPAN MARKER DETECTED:', {
                response_span: customOutputs.response_span,
                span_type: customOutputs.span_type,
                step: customOutputs.step,
                event_type: customOutputs.event_type
            });
        }

        // Check for final response span markers (schema-based)
        if (
            customOutputs?.event_type === 'craig.final.response.start' ||
            customOutputs?.event_type === 'craig.final.response.end'
        ) {
            console.log('🎯 FINAL RESPONSE SPAN MARKER (schema):', {
                event_type: customOutputs.event_type,
                full_event: event
            });
        }

        // Check for span context indicating final response
        const spanContext = (event as any).span_context;
        if (spanContext?.span_kind === 'final_response') {
            console.log('📍 Event in FINAL RESPONSE span context:', {
                span_kind: spanContext.span_kind,
                span_name: spanContext.span_name,
                event_type: customOutputs?.event_type
            });
        }

        // Check for any mention of "final_response" in event (fallback debugging)
        if (JSON.stringify(event).toLowerCase().includes('final_response')) {
            console.log('🔎 Event contains "final_response":', event);
        }

        const classification = this.classifyEvent(event);

        switch (classification) {
            case 'cognitive':
                // console.log('🧠 Routing cognitive event:', event.custom_outputs?.event_type);
                this.cognitiveEventsSubject$.next(event);
                break;

            case 'token':
                // console.log('🔤 Routing token event');
                this.tokenEventsSubject$.next(event);
                break;

            case 'datasheet':
                // console.log('📄 Routing datasheet event:', event.custom_outputs?.event_type);
                this.datasheetEventsSubject$.next(event);
                break;

            case 'completion':
                // console.log('✅ Routing completion event');
                this.completionEventsSubject$.next();
                break;

            default:
            // console.log('❓ Unknown event type:', event.type);
        }
    }

    /**
     * Classify event based on SCHEMA, not content
     * CRITICAL: This is the authoritative classification logic
     */
    private classifyEvent(event: ParsedStreamEvent): EventClassification {
        // 1. Check for completion events
        if (event.type === 'response.output_item.done') {
            return 'completion';
        }

        // 2. Check for cognitive schema (MOST RELIABLE)
        if (event.custom_outputs?.schema === 'craig.cognitive.v2.0') {
            return 'cognitive';
        }

        // 3. Check event type patterns
        const eventType = event.custom_outputs?.event_type || event.type;

        // Datasheet event types
        if (
            eventType === 'craig.datasheets.status' ||
            eventType === 'craig.datasheets.result'
        ) {
            return 'datasheet';
        }

        // Cognitive event types
        if (eventType?.startsWith('craig.cognitive.') || eventType?.startsWith('response.cognitive.')) {
            return 'cognitive';
        }

        // Token event types
        if (eventType === 'response.llm.token') {
            return 'token';
        }

        // 4. Handle response.output_text.delta based on structure
        if (event.type === 'response.output_text.delta') {
            // Has custom_outputs = cognitive
            if (event.custom_outputs && Object.keys(event.custom_outputs).length > 0) {
                return 'cognitive';
            }
            // Plain delta = token
            if (event.delta) {
                return 'token';
            }
        }
        return 'unknown';
    }
}
